import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, message, Modal, notification, Row, Select, Space, Upload } from "antd";
import { useEffect, useState } from "react";
import { CommonListQuery } from "../../../../redux/api/apiTypes";
import { useDeleteFileMutation, useLazyGetFileByObjectIdAndTypeQuery, useUploadFileForObjectMutation, useUploadFileMutation } from "../../../../redux/api/FileApi";
import { useCreateProductMutation, useLazyGetProductQuery, useUpdateProductMutation } from "../../../../redux/api/ProductApi";
import { useLazyGetProductCategoryListQuery } from "../../../../redux/api/SystemApi";
import FloatInput from "../../../common/components/FloatInput";
import FloatInputNumber from "../../../common/components/FloatInputNumber";
import FloatSelect from "../../../common/components/FloatSelect";
import FloatTextArea from "../../../common/components/FloatTextArea";
import { fileTypes, PropertyTypes } from "../../../common/types/types";
import { Product } from "../../types";
import { UploadFile } from "antd/lib/upload/interface";
import ImagePreviewModal from "../../../common/components/ImagePreviewModal";

interface ModalProps {
    visible: boolean,
    toggleModal: () => void,
    toggleCancel: () => void,
    setProduct?: (product: Product) => void,
    editId?: number,
    sellerId?: number,
    setEditId: React.Dispatch<React.SetStateAction<number>>,
    setStatuteId: React.Dispatch<React.SetStateAction<number>>,
    statuteId?: number,
}

const { confirm } = Modal;
const { Option } = Select;

const imageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
const videoTypes = ['video/mp4', 'video/wmv', 'video/wmp', '.mp4', '.wmv', '.wmp', 'video/x-ms-wmv', 'video/x-ms-asf'];
const otherTypes = ['.doc', '.docx', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.pdf', '.txt', '.xls', '.xlsx', '.ppt', '.pptx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/pdf', 'text/plain', 'application/vnd.ms-powerpoint',
    'application/vnd.ms-excel']

const AddProductModal = ({ visible, toggleModal, toggleCancel, editId, sellerId, setProduct, setEditId, statuteId, setStatuteId }: ModalProps) => {
    const [form] = Form.useForm();
    const [count, setCount] = useState<any>([]);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [fileList, setFileList] = useState<any>();
    const [defaultValues, setDefaultValues] = useState<any>();
    const [avatarId, setAvatarId] = useState(0);
    const [previewImage, setPreviewImage] = useState<string>("");
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false);

    const [categoryTrigger, categoryResponse] = useLazyGetProductCategoryListQuery()
    const [addProduct] = useCreateProductMutation();
    const [addImageToProduct] = useUploadFileForObjectMutation();
    const [fileIdTrigger] = useLazyGetFileByObjectIdAndTypeQuery();
    const [trigger, response] = useLazyGetProductQuery();
    const [deleteFile] = useDeleteFileMutation();
    const [updateProduct] = useUpdateProductMutation();
    const [uploadFile] = useUploadFileMutation();

    useEffect(() => {
        categoryTrigger({ keyword: "", page: 0, size: 1000, isPublic: 1 });
    }, [categoryTrigger])

    useEffect(() => {
        if (editId && visible) {
            trigger({ productId: editId, ...statuteId ? { statuteId } : {} }).unwrap()
                .then(res => {
                    if (res && form) {
                        let defaultValue = {
                            productType: res.productType,
                            productName: res.productName,
                            description: res.description,
                            productCategoryCode: res.categories?.[0]?.categoryId,
                            startPrice: res.startPrice
                        }
                        form.setFieldsValue(defaultValue)
                        setDefaultValues(defaultValue)
                    }
                })

            fileIdTrigger({ objectId: editId, objectType: fileTypes.PRODUCT }).unwrap()
                .then(res => {
                    if (res.length) {
                        let newFileList: any = {};

                        Promise.all(res.map(item =>
                            fetch(process.env.REACT_APP_API_URL + "/files/downloadFile/" + item.attachmentId, {
                                headers: {
                                    Authorization: 'Bearer ' + localStorage.getItem("accessToken"),
                                }
                            }).then(res => {
                                return res.blob().then(blob => {
                                    return { ...item, blob }
                                })
                            })
                        )).then(res => {
                            let promises: any = [];

                            res.forEach(item => {
                                let newPromise = new Promise(resolve => {
                                    let reader = new FileReader();
                                    reader.readAsDataURL(item.blob);
                                    reader.onload = () => resolve({ ...item, thumbUrl: reader.result });
                                })

                                promises.push(newPromise);
                            })

                            Promise.all(promises).then(fileContents => {
                                fileContents.forEach(item => {
                                    let storageType = item.storageType === "avatar" ? "image" : item.storageType;
                                    if (!newFileList[storageType]) {
                                        newFileList[storageType] = [];
                                    }

                                    newFileList[storageType].push({
                                        uid: item.attachmentId,
                                        name: item.fileName,
                                        status: 'done',
                                        thumbUrl: item.thumbUrl,
                                        response: item
                                    })

                                    if (item.storageType === "avatar") {
                                        setAvatarId(item.attachmentId);
                                    }
                                })

                                setFileList(newFileList);
                            })
                        })
                    }
                })
        }
    }, [editId, visible, trigger, fileIdTrigger, form, statuteId])

    useEffect(() => {
        if (fileList && form) {
            form.setFieldsValue({
                image_upload: fileList.image || [],
                video_upload: fileList.video || [],
                file_upload: fileList.file || []
            })

            if (fileList.image?.length) {
                let avatarImageIndex = fileList?.image
                    .findIndex((item: any) => item.response?.storageType === "avatar");
                if (avatarImageIndex !== -1) {
                    setAvatarId(fileList?.image[avatarImageIndex].response?.attachmentId || 0)
                }
            }
        }
    }, [fileList, form])

    const handlePreview = async (file: UploadFile) => {
        // if (!file.url && !file.preview) {
        //   file.preview = await getBase64(file.originFileObj as RcFile);
        // }

        setPreviewImage(file?.thumbUrl || "");
        setImagePreviewVisible(true);
    };

    const handleCancel = () => setImagePreviewVisible(false);

    const normFile = (e: any) => {
        return e.fileList;
    };

    const onFileRemove = (file: any, type: string) => {
        return new Promise<boolean>((resolve, reject) => {
            confirm({
                className: "custom-modal",
                title: 'Xoá file đã chọn?',
                onOk: () => {
                    if (file.response?.attachmentId) {
                        let newDeleteIds = deleteIds;
                        newDeleteIds.push(file.response.attachmentId)
                        setDeleteIds(newDeleteIds);
                    }
                    resolve(true);

                },
                onCancel: () => {
                    resolve(false)
                },
                okText: "Xác nhận",
                cancelText: "Hủy bỏ",
            })
        })
    }

    const beforeUpload = (files: File[], type: string) => {
        let accepted = true;
        Array.from(files).forEach(file => {
            console.log(type);
            console.log(file.type);
            if ((type === 'image' && !imageTypes.includes(file.type)) ||
                (type === 'video' && !videoTypes.includes(file.type)) ||
                (type === 'file' && !imageTypes.includes(file.type) && !videoTypes.includes(file.type) && !otherTypes.includes(file.type))) {
                accepted = false;
                notification.error({ message: 'Định dạng file không hợp lệ!' });
            }

            if ((type === 'image' && file.size / (1024 * 1024) > 5) ||
                (type === 'video' && file.size / (1024 * 1024) > 25) ||
                (type === 'file' && file.size / (1024 * 1024) > 25)) {
                accepted = false;
                notification.error({ message: 'Dung lượng file không hợp lệ' });
            }
        })

        return accepted || Upload.LIST_IGNORE;
    }

    const customUpload = (options: any, type: string) => {
        let imageUploadValues = form.getFieldValue("image_upload");
        let isAvatar = type === "image" && (imageUploadValues?.length === 1 || imageUploadValues?.[0]?.uid === options.file.uid);

        options.onProgress({ percent: 0 })
        let formData = new FormData();
        formData.append('file', options.file);
        uploadFile({
            file: formData,
            objectType: fileTypes.PRODUCT,
            storageType: isAvatar ? "avatar" : type,
        }).unwrap()
            .then(res => {
                if (isAvatar) {
                    setAvatarId(res.attachmentId)
                }
                options.onProgress({ percent: 100 });
                options.onSuccess(res, options.file);
            }).catch(err => {
                options.onError();
            })
    }

    const onFinish = (values: any) => {
        deleteIds.forEach(item => {
            deleteFile(item)
        })

        if (!editId) {
            addProduct({
                productType: values.productType,
                productName: values.productName,
                description: values.description,
                categoryIds: values.productCategoryCode,
                startPrice: values.startPrice,
                sellerId: sellerId || 0,
                status: 1,
            })
                .unwrap()
                .then(res => {
                    notification.success({
                        message: "Thêm mới tài sản thành công!"
                    })
                    addImageAfterSubmit(res, values);
                })
                .catch(err => {
                    console.log(err)
                    notification.error({
                        message: err.data?.message || "Đã có lỗi xảy ra, xin vui lòng thử lại sau!"
                    });
                })
        } else {
            updateProduct({
                productId: editId,
                productType: values.productType,
                productName: values.productName,
                description: values.description,
                categoryIds: values.productCategoryCode,
                startPrice: values.startPrice,
                sellerId: sellerId || 0,
                status: response.data?.status,
            }).unwrap()
                .then(res => {
                    notification.success({
                        message: "Cập nhật thông tin tài sản thành công!"
                    })
                    addImageAfterSubmit(res, values);
                })
                .catch(err => {
                    console.log(err)
                    notification.error({
                        message: err.data?.message || "Đã có lỗi xảy ra, xin vui lòng thử lại sau!"
                    });
                })
        }
    }

    const addImageAfterSubmit = (res: Product, values: any) => {
        let imageIds = values.image_upload?.length ? values.image_upload.filter((item: any) => item?.response?.attachmentId).map((item: any) => item.response.attachmentId) : [];
        let videoIds = values.video_upload?.length ? values.video_upload.filter((item: any) => item?.response?.attachmentId).map((item: any) => item.response.attachmentId) : [];
        let otherIds = values.file_upload?.length ? values.file_upload.filter((item: any) => item?.response?.attachmentId).map((item: any) => item.response.attachmentId) : [];

        addImageToProduct({
            listFileIds: imageIds.concat(videoIds, otherIds),
            objectId: res.productId,
            objectType: fileTypes.PRODUCT,
        })
            .unwrap()
            .then(() => {
                if (setProduct) {
                    setProduct(res);
                }
                // setDeleteIds([]);
                // setEditId(0);
                // setStatuteId(0);
                toggleModal();
            })
    }

    const onValuesChange = (changedValues: any) => {
        let key = Object.keys(changedValues)[0];
        let value = changedValues[key];
        if (typeof (value === "string")) {
            setCount({
                ...count,
                [key]: value.length,
            });
        }
    }

    const onCancel = () => {
        let values = form.getFieldsValue();
        let isDirty = false;
        if (!editId) {
            Object.keys(values).forEach(item => {
                if ((values[item] || (typeof (values[item]) === "number" && values[item] > 0) || values[item]?.length)) {
                    isDirty = true;
                }
            })
        } else {
            Object.keys(values).forEach(item => {
                if (values[item] !== defaultValues?.[item]) {
                    isDirty = true;
                }
            })
        }

        if (isDirty) {
            toggleCancel();
        } else {
            toggleModal();
        }
    }

    return (
        <Modal
            style={{ minWidth: 1000 }}
            visible={visible}
            onCancel={onCancel}
            className="custom-modal"
            footer={false}
            bodyStyle={{ padding: '24px 50px' }}
        >
            <div style={{ width: '100%', textAlign: 'center', marginTop: 20, fontSize: 30 }}>Thông tin tài sản đấu giá</div>
            <Form
                className="custom-form"
                style={{ marginTop: 35, marginBottom: 45 }}
                name="register"
                form={form}
                colon={false}
                initialValues={{
                    startPrice: 0,
                }}
                onValuesChange={onValuesChange}
                onFinish={onFinish}
            >
                <Row gutter={[30, 0]} justify="center">
                    <Col span={20} style={{ order: 1 }}>
                        <Form.Item
                            name="productName"
                            rules={[
                                { required: true, whitespace: true, message: "Tên tài sản là bắt buộc, vui lòng nhập đầy đủ." }
                            ]}>
                            <FloatInput required label="Tên tài sản" placeholder="Tên tài sản" maxLength={200} />
                        </Form.Item>
                        {/* <label className="input-show-count">{count.productName || 0}/200</label> */}
                    </Col>
                    <Col span={20} style={{ order: 2 }}>
                        <Form.Item
                            name="productType"
                            rules={[
                                { required: true, message: "Loại tài sản là bắt buộc, vui lòng nhập đầy đủ." }
                            ]}>
                            <FloatSelect required label="Loại tài sản" placeholder="Chọn loại tài sản" className="custom-select">
                                {PropertyTypes.map(item => (
                                    <Option key={item.id} value={item.id}>{item.name}</Option>
                                ))}
                            </FloatSelect>
                        </Form.Item>
                        <Form.Item
                            name="productCategoryCode"
                            rules={[
                                { required: true, message: "Nhóm tài sản là bắt buộc, vui lòng nhập đầy đủ." }
                            ]}>
                            <FloatSelect required label="Nhóm tài sản" placeholder="Chọn nhóm tài sản" className="custom-select">
                                {categoryResponse.data?.content.map(item => (
                                    <Option key={item.categoryId} value={item.categoryId}>{item.name}</Option>
                                ))}
                            </FloatSelect>
                        </Form.Item>
                    </Col>
                    <Col span={20} style={{ order: 4 }}>
                        <Form.Item
                            name="startPrice">
                            <FloatInputNumber
                                required
                                label="Giá khởi điểm"
                                placeholder="Giá khởi điểm"
                                min={Number(0)}
                                controls={false}
                                className="custom-input" />
                        </Form.Item>
                    </Col>
                    <Col span={20} style={{ order: 4 }}>
                        <Form.Item
                            rules={[
                                { required: true, message: "Mô tả tài sản là bắt buộc, vui lòng nhập đầy đủ." }
                            ]}
                            name="description">
                            <FloatTextArea required label="Mô tả tài sản" placeholder="Mô tả tài sản" maxLength={1000} style={{ height: 125 }} />
                        </Form.Item>
                        {/* <label className="input-show-count input-show-count-textarea">{count.description || 0}/1000</label> */}
                    </Col>
                    <Col span={20} style={{ order: 5 }}>
                        <div className="font-size-18">Hình ảnh tài sản<sup style={{ color: 'red' }}>*</sup>:</div>
                        <div>(Vui lòng chọn hình ảnh có định dạng JPG, JPEG, PNG có dung lượng nhỏ hơn 5 MB)</div>
                        <Form.Item
                            name="image_upload"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                                { required: true, message: "Hình ảnh tài sản là bắt buộc, vui lòng nhập đầy đủ." }
                            ]}
                        >
                            <Upload
                                onRemove={(file) => onFileRemove(file, "image")}
                                customRequest={(options) => customUpload(options, "image")}
                                accept="image/jpg,image/jpeg,image/png"
                                multiple
                                beforeUpload={(file, fileList) => beforeUpload(fileList, "image")}
                                name="image"
                                listType="picture-card"
                                onPreview={handlePreview}
                            >
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </div>
                            </Upload>
                        </Form.Item>
                    </Col>
                    <Col span={20} style={{ order: 6 }}>
                        <div className="font-size-18">Video tài sản:</div>
                        <div>(Vui lòng chọn video có định dạng WMP, MP4 có dung lượng nhỏ hơn 25 MB)</div>
                        <Form.Item
                            name="video_upload"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                        >
                            <Upload
                                onRemove={(file) => onFileRemove(file, "video")}
                                customRequest={(options) => customUpload(options, "video")}
                                accept="video/mp4,video/wmv,video/wmp,.wmv,.wmp,video/x-ms-wmv,video/x-ms-asf"
                                multiple
                                beforeUpload={(file, fileList) => beforeUpload(fileList, "video")}
                                name="video"
                                listType="picture-card">
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </div>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>
                <Row justify="center">
                    <Col span={16} style={{ order: 7, textAlign: 'center' }}>
                        <div className="font-bold font-size-16">File hồ sơ:</div>
                        <div>Các giấy tờ chứng minh người yêu cầu là người có quyền sở hữu, quyền sử dụng hoặc quyền được đấu giá tài sản theo quy định của phát luật.</div>
                        <div>Biên bản xác định giá khởi điểm của tài sản đưa ra đấu giá</div>
                        <div>.........</div>
                        <div>.........</div>
                        <div>.........</div>
                        <div>Định dạng cho phép: định dạng PDF, EXCEL, XLS, XLSX, TXT, DOC, DOCX, PPT, WMP, JPG, JPEG, PNG, MP4 và dung lượng nhỏ hơn 25MB.</div>

                        <div style={{ overflow: 'auto' }}>
                            <Form.Item
                                style={{ marginTop: 15 }}
                                name="file_upload"
                                valuePropName="fileList"
                                getValueFromEvent={normFile}>
                                <Upload
                                    className="auction-file-upload"
                                    listType="picture-card"
                                    onRemove={(file) => onFileRemove(file, "file")}
                                    customRequest={(options) => customUpload(options, "file")}
                                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                        .pdf,.txt,.xls,.xlsx,.ppt,.pptx,video/mp4,video/wmv,video/wmp,.wmv,.wmp,image/jpg,image/jpeg,image/png,image/wmp,application/vnd.ms-powerpoint,
                        application/vnd.ms-excel,video/x-ms-wmv,video/x-ms-asf"
                                    multiple
                                    beforeUpload={(file, fileList) => beforeUpload(fileList, "file")}
                                    itemRender={(originNode, file, fileList, { download, remove }) => {
                                        return (
                                            <div className="auction-file-upload-item" title={file.name}>
                                                <div style={{ fontStyle: 'italic', marginRight: 15 }} className="text-ellipsis">{file.name}</div>
                                                <div className="font-size-16" onClick={remove}>
                                                    x
                                                </div>
                                            </div>
                                        )
                                    }}
                                >
                                    <Button type="primary" style={{ background: 'white', border: '1px solid #DEDEDE', borderRadius: 10, color: "#0180C7" }}>
                                        CHỌN FILE
                                    </Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    </Col>
                </Row>
                <Row justify="center">
                    <Space>
                        <Button className="button-gray" style={{ width: 160, height: 50 }} onClick={onCancel}>HUỶ BỎ</Button>
                        <Button className="button-light-yellow" style={{ width: 160, height: 50 }} htmlType="submit">LƯU TÀI SẢN</Button>
                    </Space>
                </Row>
            </Form>

            <ImagePreviewModal
                visible={imagePreviewVisible}
                handleCancel={handleCancel}
                previewImage={previewImage}
            />
        </Modal>
    )
}

export default AddProductModal;